/**
 * Aerial Lift Cost Calculator — public calculator page (PRP-CALC-1 S3, S8).
 *
 * Recommends a lift class (scissor vs boom) from the working height and reach,
 * prices day/week/month rental from published public rate schedules, and writes
 * a justification narrative an estimator can paste into a supplement. Carries
 * the /tools SEO treatment: per-route meta + JSON-LD, coverage section, app
 * promo band.
 */
import { useState } from 'react';

import { Button } from '../../components/ui/Button';
import ToolPageShell, { DimensionDivider, Eyebrow } from './ToolPageShell';
import AppPromo from './AppPromo';
import CalcResults from './CalcResults';
import ScenarioInterview from './ScenarioInterview';
import {
  MethodSection,
  FaqSection,
  SourcesSection,
  AdvancedRegional,
  NumberField,
  SelectField,
  CheckboxField,
  type FaqItem,
} from './_shared';
import { useSeo, faqJsonLd, calculatorJsonLd } from './useSeo';
import { CalcAPI, type CalcResponse, type LiftSurface } from './calcApi';

/** What the calculator sizes and prices, phrased the way estimators search.
 * Rendered as on-page content (SEO) and an honest capability list. */
const COVERED_JOBS: { name: string; detail: string }[] = [
  {
    name: 'Reach an interior ceiling or high wall',
    detail:
      'Straight-up work on a level floor. A scissor lift gives you the widest platform for the money.',
  },
  {
    name: 'Get to a soffit or fascia over landscaping',
    detail:
      'You have to reach out and over an obstacle, so the tool moves you to a boom lift and sizes the reach.',
  },
  {
    name: 'Work above a lower roof',
    detail:
      'An articulating boom clears the lower roof and drops the platform back down to the wall above it.',
  },
  {
    name: 'Reach a two- or three-story exterior wall',
    detail:
      'Working height sets the class. The tool adds the 6-foot person reach so you do not under-size it.',
  },
  {
    name: 'Rough-terrain exterior access',
    detail:
      'Soft or uneven ground rules out an electric slab machine and steps you up to a rough-terrain unit.',
  },
  {
    name: 'Indoor work with no exhaust',
    detail:
      'Checking the indoor box keeps the recommendation to an electric lift with non-marking tires.',
  },
];

const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'How much does it cost to rent a boom lift or scissor lift?',
    a: 'From published 2026 rental data, a 19 to 20 foot electric scissor lift runs about $100 to $150 per day, roughly $300 to $450 per week, and $700 to $1,100 per month. A 40 to 60 foot boom lift runs about $350 to $700 per day, $1,000 to $2,200 per week, and $3,000 to $6,500 per month. Delivery and pickup are billed on top, usually $100 to $300 each way depending on distance. These are planning ranges from public rate schedules, not a quote. The calculator prices your specific working height and duration at the tier it falls into.',
  },
  {
    q: 'Scissor lift or boom lift?',
    a: 'Use a scissor lift when you only need to go straight up on a firm, level floor. It gives you a wide, stable platform at the lowest rental cost. Use a boom lift when you have to reach out and over something, or up and back down to a wall above a lower roof. The tool reads your working height and horizontal reach and picks the smallest class that covers both.',
  },
  {
    q: 'What is the difference between working height and platform height?',
    a: 'Platform height is how high the floor of the basket goes. Working height adds about 6 feet for a person reaching overhead, so a 20 foot platform is a 26 foot working height. Under-sizing a lift by entering platform height instead of working height is the most common mistake estimators make. Always size to the working height you need.',
  },
  {
    q: 'When do I need a rough-terrain lift instead of an electric one?',
    a: 'Electric scissor and boom lifts are built for smooth slabs and firm, level ground, and they run quiet with no exhaust so they work indoors. Rough terrain, soft soil, or a grade needs an engine-powered rough-terrain unit with larger tires and often four-wheel drive. It costs more to rent, so the tool only steps you up to it when the surface calls for it.',
  },
  {
    q: 'Why is a weekly rental cheaper than seven daily rentals?',
    a: 'Rental rates step down at the daily, weekly, and monthly break points. A one-week rental almost always costs less than seven separate daily rentals, and a month is cheaper than four weeks. The tool prices the duration you enter at the correct tier instead of multiplying the day rate.',
  },
  {
    q: 'Are delivery and pickup included in the rate?',
    a: 'No. Rental yards bill delivery and pickup as separate charges, usually a flat fee each way that depends on distance and the size of the machine. Budget for both. A one-day rental can cost as much in freight as in rental time, which is another reason to combine the work into one trip.',
  },
  {
    q: 'Can I use this number in an Xactimate supplement?',
    a: 'Yes. The result includes the lift class, the working-height rationale, the rate source, and a narrative you can paste into the supplement. Where a matching Xactimate equipment item exists (EQU SLIFT for a scissor lift, EQU BLIFT for a boom lift), the tool names it so the line prices regionally at import.',
  },
  {
    q: 'Is this a quote?',
    a: 'No. It is a planning estimate built from published public rate schedules. Confirm the machine, access, and price with a rental yard before you rely on it.',
  },
];

export default function AerialLiftCalculatorPage() {
  useSeo({
    title:
      'Aerial Lift Cost Calculator — Boom & Scissor Lift Rental | Antero Trail',
    description:
      'Free aerial lift cost calculator. Pick the right scissor or boom lift for a working height and price day, week, and month rental from published rate data for restoration claims.',
    path: '/tools/aerial-lift-cost-calculator',
    jsonLd: [
      calculatorJsonLd({
        name: 'Aerial Lift Cost Calculator',
        description:
          'Recommends a scissor or boom lift from the working height and reach, prices day/week/month rental from published public rate schedules, and writes a justification narrative for restoration supplements.',
        path: '/tools/aerial-lift-cost-calculator',
      }),
      faqJsonLd(FAQ_ITEMS),
    ],
  });

  const [mode, setMode] = useState<'scenario' | 'advanced'>('scenario');
  const [workingHeight, setWorkingHeight] = useState<number>(26);
  const [reach, setReach] = useState<number | ''>('');
  const [indoor, setIndoor] = useState(false);
  const [surface, setSurface] = useState<LiftSurface>('slab');
  const [weeks, setWeeks] = useState<number>(1);
  const [regional, setRegional] = useState(0);

  const [result, setResult] = useState<CalcResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const res = await CalcAPI.lift(
        {
          working_height_ft: workingHeight,
          ...(reach !== '' ? { horizontal_reach_ft: reach } : {}),
          indoor,
          surface,
          duration_weeks: weeks,
        },
        regional,
      );
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      setResult(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <ToolPageShell>
      <Eyebrow>Lifting &amp; access — free calculator</Eyebrow>
      <h1 className="mt-2 font-ui text-4xl font-bold tracking-tight sm:text-[2.75rem] sm:leading-tight">
        Aerial Lift Cost Calculator
      </h1>
      <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-muted-foreground">
        Pick the right scissor or boom lift for a restoration task and estimate
        the rental cost from published public rate schedules. Enter how high you
        need to work, whether you have to reach out over an obstacle, and the
        site conditions — the tool recommends the smallest lift that covers the
        job and prices it by the day, week, or month. Or describe the task in
        plain terms and let it derive the numbers.
      </p>

      {/* Mode toggle: describe the job (S8 scenario) vs raw parameters */}
      <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Input mode">
        <Button
          type="button"
          role="tab"
          aria-selected={mode === 'scenario'}
          variant={mode === 'scenario' ? 'primary' : 'outline'}
          onClick={() => setMode('scenario')}
        >
          Describe the job
        </Button>
        <Button
          type="button"
          role="tab"
          aria-selected={mode === 'advanced'}
          variant={mode === 'advanced' ? 'primary' : 'outline'}
          onClick={() => setMode('advanced')}
        >
          Advanced mode (engineering inputs)
        </Button>
      </div>

      {mode === 'scenario' && (
        <div className="mt-6 max-w-3xl rounded-lg border border-border bg-card p-5 sm:p-6">
          <ScenarioInterview calculator="lift" />
        </div>
      )}

      <div
        className={mode === 'advanced' ? 'mt-8 grid gap-8 lg:grid-cols-2' : 'hidden'}
      >
        {/* Form */}
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg border border-border bg-card p-5 sm:p-6"
          aria-label="Lift inputs"
        >
          <NumberField
            label="Working height"
            suffix="ft"
            value={workingHeight}
            min={1}
            onChange={setWorkingHeight}
            hint="Height you need to work at, not the platform height. Working height is roughly platform height plus 6 feet."
          />
          <NumberField
            label="Horizontal reach"
            suffix="ft"
            value={reach}
            min={0}
            onChange={(v) => setReach(Number.isNaN(v) ? '' : v)}
            hint="Optional. How far out you must reach over an obstacle. Leave blank for straight-up work."
          />
          <SelectField<LiftSurface>
            label="Surface"
            value={surface}
            onChange={setSurface}
            options={[
              { value: 'slab', label: 'Slab / smooth concrete' },
              { value: 'firm', label: 'Firm ground' },
              { value: 'rough', label: 'Rough terrain' },
            ]}
          />
          <NumberField
            label="Rental duration"
            suffix="weeks"
            value={weeks}
            min={1}
            step="1"
            onChange={setWeeks}
            hint="Rates step down at the weekly and monthly break points."
          />
          <div className="pt-1">
            <CheckboxField
              label="Indoor use"
              checked={indoor}
              onChange={setIndoor}
            />
          </div>

          <AdvancedRegional value={regional} onChange={setRegional} />

          <Button type="submit" isLoading={loading} className="w-full">
            Recommend a lift
          </Button>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
        </form>

        {/* Results */}
        <div>
          {result ? (
            <CalcResults result={result} />
          ) : (
            <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
              Enter your task and select Recommend a lift to see a lift class, a
              cost range, and a justification narrative.
            </div>
          )}
        </div>
      </div>

      <section className="mt-14">
        <DimensionDivider label="What it covers" />
        <h2 className="mb-3 mt-5 font-ui text-2xl font-bold tracking-tight">
          What the lift calculator sizes and prices
        </h2>
        <p className="mb-5 max-w-3xl text-[15px] leading-relaxed text-muted-foreground">
          The calculator starts from working height, the number that matters,
          and adds the 6-foot person reach so you do not size to the platform by
          mistake. It reads your horizontal reach to choose between a scissor
          and a boom, factors the surface into electric versus rough-terrain,
          and prices the duration at the daily, weekly, or monthly tier it falls
          into. It covers these tasks today:
        </p>
        <ul className="grid max-w-4xl gap-x-8 gap-y-3 sm:grid-cols-2">
          {COVERED_JOBS.map((job) => (
            <li key={job.name} className="flex gap-3">
              <span
                aria-hidden
                className="mt-2 h-2.5 w-px shrink-0 bg-amber-500"
              />
              <span className="text-sm leading-relaxed">
                <span className="font-semibold text-foreground">
                  {job.name}.
                </span>{' '}
                <span className="text-muted-foreground">{job.detail}</span>
              </span>
            </li>
          ))}
        </ul>
        <p className="mt-5 max-w-3xl text-sm leading-relaxed text-muted-foreground">
          A specialty-access branch for confined or restricted sites, such as
          spider lifts and low-clearance machines, is coming. For now the tool
          covers standard scissor and boom classes.
        </p>
      </section>

      <AppPromo
        className="mt-14"
        context="In the app, the lift lands in your estimate as Xactimate EQU SLIFT or BLIFT items sized to the working height, with the reasoning attached — no copy-paste."
      />

      <MethodSection title="How the lift is selected and priced">
        <p>
          The choice between a scissor lift and a boom lift comes down to
          whether you need to reach out or only up. A scissor lift raises a wide
          platform straight up and is the cheaper, more stable choice for
          straight vertical work such as ceilings and interior walls. A boom
          lift, either articulating or telescopic, trades cost for the ability
          to reach up and over an obstacle, which is what you need to get to a
          soffit past landscaping or to a wall above a lower roof. The tool
          reads your working height and horizontal reach and recommends the
          smallest class that covers both.
        </p>
        <p>
          Working height is the number that matters, not platform height. A
          person working from a platform reaches about 6 feet above it, so a 20
          foot platform is a 26 foot working height. Getting this wrong is the
          most common way estimators under-size a lift. Site conditions change
          the machine too: indoor work and smooth slabs allow an electric lift
          with non-marking tires, while rough terrain needs a heavier
          engine-powered unit that rents for more. Cost comes from published
          equipment rate schedules and steps down at the daily, weekly, and
          monthly break points, so the tool prices the duration you enter at the
          rate tier it falls into.
        </p>
        <p>
          Delivery and pickup are billed on top of the rental rate, each way, so
          the result shows them as separate line items rather than folding them
          into the day rate. That keeps the freight visible, which matters most
          on short rentals where it can rival the rental time itself.
        </p>
      </MethodSection>

      <FaqSection items={FAQ_ITEMS} />

      <SourcesSection
        sources={[
          {
            name: 'Published 2026 aerial lift rental rate data',
            detail:
              'Day, week, and month rental bands for scissor and boom classes from published rental sources (such as DOZR and Rent1USA), used for the market cost range.',
          },
          {
            name: 'Caltrans Labor Surcharge and Equipment Rental Rates',
            detail:
              'Published equipment rental rate schedule used as the public basis for aerial lift rental rates.',
          },
          {
            name: 'USACE equipment rate references',
            detail:
              'Federal equipment cost references used to cross-check the rental basis.',
          },
          {
            name: 'ANSI A92 and OSHA 1926 Subpart L',
            detail:
              'Aerial work platform classification and safe-use requirements that inform the scissor versus boom recommendation.',
          },
        ]}
      />
    </ToolPageShell>
  );
}
